// From https://www.redblobgames.com/maps/terrain-from-noise/
// Copyright 2015 Red Blob Games <redblobgames@gmail.com>
// License: Apache v2.0 <https://www.apache.org/licenses/LICENSE-2.0.html>

console.info("I'm happy to answer questions about the code; email me at redblobgames@gmail.com");

import SvgRemappingChart from "./visualize-reshaper";
import Map2D from "./map2d";
import Renderer2D from "./render2d";
import Renderer3D from "./render3d";
import RendererBoth from "./renderboth";
import { SEED, SimplexNoise2D, SimplexNoise3D, SimplexNoise4D, makeNoise,
         fillNoise, mixNoise, reshapeNoiseExponent } from "./noise-utils";
import * as Colormap from "./colormap";

/** Convenience wrapper around slider controls:

    There should be an <input id=$elementId type=range ...>
    and there can be any number of <span class=$elementId>
    to receive the output values
 */
class Slider {
    constructor(elementId, callback) {
        this.callback = callback;
        this.elementId = elementId;
        this.slider = document.getElementById(elementId);
        this.slider.addEventListener('input', () => this.updateFromSlider());
        this.digits = 2;
        this.value = this.slider.valueAsNumber;
        this.min = this.slider.min;
        this.max = this.slider.max;

        this.outputs = document.querySelectorAll("." + this.elementId);
        this.constructScrubbables();

        this.updating = false;
    }

    updateFromSlider() {
        if (!this.updating) {
            this.updating = true;
            requestAnimationFrame(() => {
                this.updating = false;
                this.value = this.slider.valueAsNumber;
                this.updateOutputs();
                this.callback();
            });
        }
    }

    constructScrubbables() {
        // See https://www.redblobgames.com/making-of/draggable/
        let dragging = null;
        for (let el of this.outputs) {
            const start = (event) => {
                if (event.button !== 0 || event.ctrlKey) return;
                dragging = {initialValue: this.value,
                            startingPosition: event.clientX};
                this.outputs.forEach((e) => e.classList.add('dragging'));
                event.currentTarget.setPointerCapture(event.pointerId);
            };
            const stop = (event) => {
                dragging = false;
                this.outputs.forEach((e) => e.classList.remove('dragging'));
            };
            const move = (event) => {
                if (!dragging) return;
                const pixelsForEntireRange = 200;
                const scale = pixelsForEntireRange / (this.max - this.min);
                this.slider.value = dragging.initialValue +
                    (event.clientX - dragging.startingPosition) / scale;
                this.updateFromSlider();
            };
            const cancel = (event) => {
                event.preventDefault();
            };

            el.addEventListener('pointerdown', start);
            el.addEventListener('pointerup', stop);
            el.addEventListener('pointercancel', stop);
            el.addEventListener('pointermove', move);
            el.addEventListener('dragstart', cancel);
            el.addEventListener('touchstart', cancel);

            el.classList.add('scrubbable');
        }
    }

    updateOutputs() {
        for (let el of this.outputs) {
            el.textContent = this.value.toFixed(this.digits);
            el.classList.toggle('at-min', this.value <= this.min);
            el.classList.toggle('at-max', this.value >= this.max);
        }
    }
}



// Static diagram: noise
(function() {
    const map = makeNoise(300, 100, 3, SEED);
    new Renderer2D('render-noise', map, Colormap.gray()).draw();
})();


// Static diagram: noise drawn as elevation
(function() {
    const map = makeNoise(150, 100, 2, SEED);
    new RendererBoth('render-elevation', map, Colormap.greengray()).draw();
})();


// Dynamic diagram: show frequency
(function() {
    let map = new Map2D(150, 100);
    let slider = new Slider('noise-frequency', redraw);
    let renderer = new RendererBoth('render-noise-frequency', map, Colormap.greengray());
    function redraw() {
        let frequency = slider.value;
        fillNoise(map, frequency, SEED);
        renderer.draw();
    }
    redraw();
})();


// Static diagram: add frequencies together
(function() {
    new Renderer2D('render-addition-1', makeNoise(100, 100, 2, SEED), Colormap.gray()).draw();
    new Renderer2D('render-addition-2', makeNoise(100, 100, 8, SEED+1), Colormap.gray()).draw();
    new Renderer2D('render-addition-3', makeNoise(100, 100, 32, SEED+2), Colormap.gray()).draw();
    let map = new Map2D(100, 100);
    mixNoise(map, [1, 1/2, 1/4, 1/8, 1/16], 1);
    new Renderer2D('render-addition-sum', map, Colormap.gray()).draw();
})();


// Dynamic diagram: show mixing of frequencies
(function() {
    let map = new Map2D(150, 100);
    let slider = new Slider('noise-fbm-mix', redraw);
    let renderer = new RendererBoth('render-noise-fbm', map, Colormap.greengray());
    function redraw() {
        // The spectrum displayed always includes the base octave, but
        // then linearly scales the other octaves from 0 to 100%.
        const spectrum = [1, slider.value/2, slider.value/4];
        mixNoise(map, spectrum, 2);
        renderer.draw();
    }
    redraw();
})();


// Dynamic diagram: show exponent applied to elevation
(function() {
    let map1 = new Map2D(150, 100);
    let map2 = new Map2D(150, 100);
    let slider = new Slider('noise-exponent', redraw);
    let renderer = new Renderer3D('render-exponent-3d', map2, Colormap.greengray());
    let svgChart = new SvgRemappingChart("#chart-exponent");
    function redraw() {
        const exponent = slider.value;
        const transform = e => Math.pow(e, exponent);
        mixNoise(map1, [1, 1/4, 1/8, 1/16], 2);
        reshapeNoiseExponent(map2, map1, exponent);
        renderer.draw();
        svgChart.setFn(transform);
    }
    redraw();
})();


// Dynamic diagram: show water level
(function() {
    const exponent = 3;
    let map1 = new Map2D(150, 100);
    let map2 = new Map2D(150, 100);
    let colors = Colormap.waterlevel(Colormap.greengray());
    let slider = new Slider('waterlevel', redraw);
    let renderer = new RendererBoth('render-waterlevel', map2, colors);
    function redraw() {
        let level = Math.pow(slider.value, exponent);
        mixNoise(map1, [1, 1/2, 1/4], 2);
        reshapeNoiseExponent(map2, map1, exponent);
        for (let id = 0; id < map2.size; id++) {
            if (map2.data[id] < level) { map2.data[id] = level-(1/255); }
        }
        colors.setLevel(level*255);
        renderer.draw();
    }
    redraw();
})();


// Static diagram: show terrain bands
(function() {
    const exponent = 5;
    let map1 = new Map2D(150, 100);
    let map2 = new Map2D(150, 100);
    let colors = Colormap.terrainbands();
    let renderer = new RendererBoth('render-terrainbands', map2, colors);
    mixNoise(map1, [1, 1/4, 1/8, 1/16], 2);
    reshapeNoiseExponent(map2, map1, exponent);
    renderer.draw();
})();


// Static diagram: show E and M noise
(function() {
    const exponent = 5;
    let elevation1 = new Map2D(150, 100);
    let elevation2 = new Map2D(150, 100);
    let moisture = new Map2D(150, 100);
    let rendererE = new Renderer2D('render-both-elevation', elevation2, Colormap.greengray());
    let rendererM = new Renderer2D('render-both-moisture', moisture, Colormap.grayblue());
    
    mixNoise(elevation1, [1, 1/4, 1/8, 1/16], 2);
    reshapeNoiseExponent(elevation2, elevation1, exponent);
    rendererE.draw();
    
    mixNoise(moisture, [1, 1/2, 1/4], 2, 5551212);
    rendererM.draw();
})();


// Static diagram: show discrete biomes
(function() {
    const exponent = 5;
    let elevation = new Map2D(150, 100);
    let moisture = new Map2D(150, 100);
    let height = new Map2D(150, 100);
    let colors = Colormap.discretebiomes(elevation, moisture);
    let renderer = new Renderer3D('render-discretebiomes-3d', height, colors);
    mixNoise(elevation, [1, 1/4, 1/8, 1/16], 2);
    reshapeNoiseExponent(height, elevation, exponent);
    
    mixNoise(moisture, [1, 1/2, 1/4], 2, 5551212);
    
    renderer.draw();
})();


// Dynamic diagram: show island reshaping
(function() {
    let seed = 1991; // nice default seed
    let elevation = new Map2D(300, 200);
    let height = new Map2D(300, 200);
    let colors = Colormap.waterlevel(Colormap.greengray());
    colors.setLevel(127);
    let renderer = new RendererBoth('render-island', height, colors);
    let shape = new Slider('island-shape', redraw);

    const distanceFns = {
        SquareBump: (nx, ny) => 1 - (1-nx*nx) * (1-ny*ny),
        EuclideanSquared: (nx, ny) => Math.min(1, (nx*nx + ny*ny) / Math.sqrt(2)),
        Diagonal: (nx, ny) => Math.max(Math.abs(nx), Math.abs(ny)),
        Manhattan: (nx, ny) => (Math.abs(nx) + Math.abs(ny)) / 2,
        Euclidean: (nx, ny) => Math.hypot(nx, ny) / Math.sqrt(2),
        Hyperboloid: (nx, ny) => (Math.hypot(nx, ny, 0.2) - 0.2) / (Math.hypot(1, 1, 0.2) - 0.2),
        Blob: (nx, ny) => Math.hypot(nx, ny-0.05)**2 / Math.sqrt(2) * 2.7 / (3 - Math.sin(5 * Math.atan2(ny-0.05, nx))),
    };

    const reseedButton = document.querySelector("#island-reseed");
    reseedButton.addEventListener('click', () => { seed = Math.random()*10000|0; generate(); });
    
    const distanceSelector = document.querySelector("#island-distance-fn");
    distanceSelector.innerHTML = Object.keys(distanceFns).map(name => `<option value="${name}">${name}</option>`).join("");
    distanceSelector.addEventListener('change', redraw);
    
    function lerp(a, b, t) { return a * (1-t) + b * t; }
    function reshape(e, d) { return lerp(e, 1-d, shape.value); }

    function redraw() {
        let distanceFn = distanceFns[distanceSelector.value ?? 'SquareBump'];
        for (let y = 0; y < elevation.h; y++) {
            for (let x = 0; x < elevation.w; x++) {
                // Transform the height to make islands
                let nx = 2*(x/elevation.w) - 1;
                let ny = 2*(y/elevation.h) - 1;
                let e = elevation.get(x, y);
                let d = distanceFn(nx, ny);
                if (d < 0) d = 0;
                if (d > 1) d = 1;
                e = reshape(e, d);
                height.set(x, y, e);
            }
        }
        renderer.draw();
    }
    
    function generate() {
        mixNoise(elevation, [1, 1/2, 1/4, 1/8, 1/16], 2, seed);
        redraw();
    }

    generate();
    
    // Also draw the SVG linear conversion diagram
    function updateSvgLinearConversion() {
        let g = document.querySelector("#diagram-linear-conversion g");
        let html = ``;
        for (let {x, f, label} of
             [{x: 25, f: level => level/2, label: "Force land"},
              {x: 150, f: level => level/2+25, label: ""},
              {x: 275, f: level => level/2+50, label: "Force water"}]) {
            html += `<g transform="translate(${x}, 0)">`;
            for (let level = 5; level <= 95; level += 10) {
                html += `<path d="M 0 ${level} C 50 ${level} 50 ${f(level)} 100 ${f(level)}" />`;
            }
            html += `<text stroke="none" fill="black" font-size="10" x="50" y="110" text-anchor="middle">${label}</text>`;
            html += `</g>`;
        }
        g.innerHTML = html;
    }
    
    updateSvgLinearConversion();
})();


// Dynamic diagram: show terraces
(function() {
    const exponent = 3;
    let elevation = new Map2D(300, 150);
    let height = new Map2D(300, 150);
    let terraced = new Map2D(300, 150);
    let colors = Colormap.waterlevel(Colormap.terrainbands());
    colors.setLevel(20);
    let renderer = new Renderer3D('render-terraces-3d', terraced, colors);
    let slider_numlevels = new Slider('terraces-numlevels', redraw); slider_numlevels.digits = 0; slider_numlevels.updateOutputs();
    let svgChart = new SvgRemappingChart("#chart-terraces");

    mixNoise(elevation, [1, 1/4, 1/8, 1/16], 1, 5551215);
    reshapeNoiseExponent(height, elevation, exponent);

    function redraw() {
        const numlevels = slider_numlevels.value;
        const transform = e => Math.round(e * numlevels) / numlevels;
        for (let y = 0; y < height.h; y++) {
            for (let x = 0; x < height.w; x++) {
                terraced.set(x, y, transform(height.get(x, y)));
            }
        }
        renderer.draw();
        svgChart.setFn(transform);
    }
    redraw();
})();


// Dynamic diagram: blue noise to trees
(function() {
    let bluenoise = new Map2D(150, 150);
    let trees = new Map2D(150, 150);
    let renderer_in = new Renderer2D('render-trees-2d-in', bluenoise, Colormap.grayblue());
    let renderer_out = new Renderer2D('render-trees-2d-out', trees, Colormap.gray());
    let slider_radius = new Slider('trees-radius', redraw); slider_radius.digits = 0; slider_radius.updateOutputs();
    mixNoise(bluenoise, [1/2, 0, 0, 0, 0, 0, 0, 0, 1], 1/6, 5551215);
    renderer_in.draw();

    function redraw() {
        let R = slider_radius.value;
        for (let yc = 0; yc < bluenoise.h; yc++) {
            for (let xc = 0; xc < bluenoise.w; xc++) {
                let max = 0;
                for (let dy = -R; dy <= R; dy++) {
                    for (let dx = -R; dx <= R; dx++) {
                        let xn = dx + xc, yn = dy + yc;
                        // optionally check that (dx*dx + dy*dy <= R * (R + 1))
                        if (0 <= yn && yn < bluenoise.h && 0 <= xn && xn < bluenoise.w) {
                            let e = bluenoise.get(xn, yn);
                            if (e > max) { max = e; }
                        }
                    }
                }
                trees.set(xc, yc, bluenoise.get(xc, yc) === max? 0 : 255);
            }
        }
        renderer_out.draw();
    }
    redraw();
})();


// Static diagram: variable density trees
(function() {
    const exponent = 5;
    let elevation = new Map2D(300, 150);
    let height = new Map2D(300, 150);
    let moisture = new Map2D(300, 150);
    let bluenoise = new Map2D(300, 150);
    let trees = new Map2D(300, 150);
    let renderer_terrain = new Renderer2D('render-trees-2d-terrain', elevation,
                                         Colormap.discretebiomes(elevation, moisture));
    let renderer_trees = new Renderer2D('render-trees-2d-variable-density-trees',
                                        trees, Colormap.gray());
    
    mixNoise(bluenoise, [1], 40, 5551215);
    mixNoise(elevation, [1, 1/4, 1/8, 1/16], 2);
    reshapeNoiseExponent(height, elevation, exponent);
    mixNoise(moisture, [1, 1/2, 1/4], 2, 5551212);

    for (let yc = 0; yc < bluenoise.h; yc++) {
        for (let xc = 0; xc < bluenoise.w; xc++) {
            let max = 0;
            let R = Math.min(10, Math.round(0.1 + 0.4 / Math.max(0.1, moisture.get(xc, yc) - 0.5*elevation.get(xc, yc))));
            if (elevation.get(xc, yc) < 0.32) { max = Infinity; }
            for (let yn = yc - R; yn <= yc + R; yn++) {
                for (let xn = xc - R; xn <= xc + R; xn++) {
                    let e = bluenoise.get(xn, yn);
                    if (e > max) { max = e; }
                }
            }
            trees.set(xc, yc, bluenoise.get(xc, yc) == max ? 0 : 255);
        }
    }
    
    renderer_terrain.draw();
    renderer_trees.draw();

    // Combine the two images into one; since the images are cached
    // I'm using those instead of re-rendering the canvases here. I
    // had to mark them with an id in the html. However we have to
    // wait for both images to load.
    let image1 = document.getElementById(renderer_terrain.elementId);
    let image2 = document.getElementById(renderer_trees.elementId);
    function buildCompositeImage() {
        if (!image1.complete || !image2.complete) {
            setTimeout(buildCompositeImage, 100);
            return;
        }
        let composite = document.getElementById('render-trees-2d-composite');
        let ctx = composite.getContext('2d');
        ctx.drawImage(image1, 0, 0);
        ctx.globalCompositeOperation = 'multiply';
        ctx.drawImage(image2, 0, 0);
    }
    buildCompositeImage();
})();


// Static diagram: show smooth biomes
(function() {
    const exponent = 5;
    let elevation = new Map2D(150, 100);
    let moisture = new Map2D(150, 100);
    let height = new Map2D(150, 100);
    let colors = Colormap.smoothbiomes(elevation, moisture);
    let renderer = new Renderer3D('render-smoothbiomes-3d', height, colors);
    mixNoise(elevation, [1, 1/4, 1/8, 1/16], 2);
    reshapeNoiseExponent(height, elevation, exponent);
    
    mixNoise(moisture, [1, 1/2, 1/4], 2, 5551212);
    
    renderer.draw();
})();


// Static diagram: show how E+M become a biome. X axis is M, Y axis is E.
(function() {
    let elevation = new Map2D(100, 100);
    let moisture = new Map2D(100, 100);
    let colors1 = Colormap.smoothbiomes(elevation, moisture);
    let colors2 = Colormap.discretebiomes(elevation, moisture);
    let renderer1 = new Renderer2D('biome-lookup-smooth', elevation, colors1);
    let renderer2 = new Renderer2D('biome-lookup-discrete', elevation, colors2);

    for (let y = 0; y < elevation.h; y++) {
        for (let x = 0; x < elevation.w; x++) {
            // These undo the reshaping in the color function :-/
            elevation.set(x, y, (1.35 - y/elevation.h) / 1.4);
            moisture.set(x, y, (1.0 + x/elevation.w) / 3.0);
        }
    }
    
    renderer1.draw();
    renderer2.draw();
})();


// Dynamic diagram: show temperature set from both latitude and elevation
(function() {
    // NOTE: due to the stupid way I set up the colormaps, the
    // land/water transition happens at some weird number
    const sea_level = 0.347;
    const exponent = 5;
    
    let elevation = new Map2D(300, 150);
    let moisture = new Map2D(300, 150);
    let height = new Map2D(elevation.w, elevation.h);
    let invtemperature = new Map2D(elevation.w, elevation.h);
    let colors = Colormap.smoothbiomes(invtemperature, moisture);
    let renderer = new Renderer2D('render-latitude-2d', height, colors);
    let slider_equator = new Slider('latitude-equator', redraw);
    let slider_poles = new Slider('latitude-poles', redraw);

    mixNoise(elevation, [1, 1/2, 1/3, 1/4, 1/5], 3);
    // lower elevation to make more oceans and less land - tweaking here :(
    for (let y = 0; y < elevation.h; y++) {
        for (let x = 0; x < elevation.w; x++) {
            elevation.set(x, y, 1.7 * elevation.get(x, y) - 0.5);
        }
    }
    reshapeNoiseExponent(height, elevation, exponent);
            
    mixNoise(moisture, [1, 1/2, 1/3, 1/4, 1/5], 2, 5551212);
    
    function redraw() {
        const a = slider_equator.value, b = slider_poles.value;
        for (let y = 0; y < elevation.h; y++) {
            for (let x = 0; x < elevation.w; x++) {
                let e = elevation.get(x, y);
                let e0 = e - sea_level;
                if (e0 >= 0) {
                    // lots of tweaking needed here :(
                    e0 = 10*e0*e0 + b + (a-b) * Math.sin(Math.PI * (y / elevation.h));
                    if (e0 < 0) { e0 = 0; }
                }
                invtemperature.set(x, y, sea_level + e0);
            }
        }
        renderer.draw();
    }

    redraw();
})();


// Static diagram: demo at top
(function() {
    const exponent = 5;
    let elevation = new Map2D(300, 200);
    let moisture = new Map2D(300, 200);
    let height = new Map2D(300, 200);
    let colors = Colormap.smoothbiomes(elevation, moisture);
    let renderer = new Renderer3D('topdemo-3d', height, colors);
    mixNoise(elevation, [1, 1/2, 1/4, 1/8, 1/16], 2);
    reshapeNoiseExponent(height, elevation, exponent);
    
    mixNoise(moisture, [1, 1/2, 1/3, 1/4, 1/5], 2, 5551212);
    
    renderer.draw();
})();


// Dynamic diagram: demo at bottom
(function() {
    let elevation = new Map2D(300, 200);
    let moisture = new Map2D(300, 200);
    let height = new Map2D(300, 200);
    let colors = Colormap.smoothbiomes(elevation, moisture);
    let renderer = new Renderer3D('finaldemo-3d', height, colors);

    // Make lots of sliders
    let s = {};
    ['e1', 'e2', 'e3', 'e4', 'e5', 'e6',
     'm1', 'm2', 'm3', 'm4', 'm5', 'm6',
     'exponent',
    ].forEach(function (name) {
        s[name] = new Slider('final-' + name, redraw);
        s[name].updateOutputs();
    });
    
    function redraw() {
        mixNoise(elevation, [s.e1.value, s.e2.value, s.e3.value, s.e4.value, s.e5.value, s.e6.value], 2);
        reshapeNoiseExponent(height, elevation, s.exponent.value);
        mixNoise(moisture, [s.m1.value, s.m2.value, s.m3.value, s.m4.value, s.m5.value, s.m6.value], 2, 5551212);
        renderer.draw();
    }
    redraw();
})();


// Dynamic diagram: show ridged noise, with amplitudes depending on the
// elevation calculated from lower frequencies
(function() {
    const exponent = 3;
    let noise = SimplexNoise3D(SEED);
    let elevation = new Map2D(300, 150);
    const aspect = elevation.w / elevation.h;
    let moisture = new Map2D(150, 150);
    mixNoise(moisture, [1, 1/2, 1/3, 1/4, 1/5, 1/6], 1, 5551216);
    let colors = Colormap.smoothbiomes(elevation, moisture);
    let renderer3d = new Renderer3D('render-ridged-3d', elevation, colors);
    let slider_numlevels = new Slider('ridged-numlevels', redraw); slider_numlevels.digits = 0; slider_numlevels.updateOutputs();
    let svgChart = new SvgRemappingChart("#chart-ridged", {step: 0.5});
    svgChart.setFn(e => 2 * (0.5 - Math.abs(0.5-e)));
    
    function redraw() {
        const {h, w} = elevation;
        let numlevels = slider_numlevels.value;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < elevation.w; x++) {
                let z = 0, scale = 0, nx = x/w - 0.5, ny = y/h - 0.5;
                for (let octave = 0, frequency = 1; octave < numlevels; octave++, frequency *= 2) {
                    let amplitude = 1/frequency;
                    if (octave > 0) amplitude *= z;
                    scale += amplitude;
                    z += amplitude * (1 - Math.abs(noise(nx * aspect * frequency, ny * frequency, octave)));
                }
                elevation.set(x, y, 0.2 + Math.pow(z/scale, exponent));
            }
        }
        renderer3d.draw();
    }
    redraw();
})();


/** This version takes dx, dy parameters for scrolling x, y */
function mixNoiseWithOffset(output, spectrum, frequency, seed) {
    let aspect = output.w / output.h;
    
    let scale = 0.0;
    spectrum.forEach(function(amplitude) { scale += amplitude; });
    
    let noise = spectrum.map(function(_, octave) {
        return SimplexNoise2D(seed + octave);
    });

    return function(dx, dy) {
        for (let y = 0; y < output.h; y++) {
            for (let x = 0; x < output.w; x++) {
                let nx = dx + x/output.w - 0.5, ny = dy + y/output.h - 0.5;
                let z = 0.0;
                for (let octave = 0, exponent = 1; octave < spectrum.length; octave++, exponent *= 2) {
                    z += spectrum[octave] * ((noise[octave](nx * frequency * aspect * exponent, ny * frequency * exponent))/2 + 0.5);
                }
                output.set(x, y, z/scale);
            }
        }
    };
}


// Static diagram: wraparound maps
(function() {
    const TAU = 2 * Math.PI;
    const seed = 21; // hand chosen to produce maps with good features using simplex-noise@3.0
    const spectrum = [1, 1, 1/2, 1/4]; // limit detail to make the big picture more obvious
    let scale = spectrum.reduce((x, y) => x + y, 0) * 1.2;
    let eNoise3 = spectrum.map((_, octave) => SimplexNoise3D(seed + octave));
    let eNoise4 = spectrum.map((_, octave) => SimplexNoise4D(seed + octave));
    let mNoise3 = spectrum.map((_, octave) => SimplexNoise3D(seed + octave + 100));
    let mNoise4 = spectrum.map((_, octave) => SimplexNoise4D(seed + octave + 100));

    // Higher dimensional noise tends to have a narrower range, so I try to compensate by increasing
    // the range here. See https://digitalfreepen.com/2017/06/20/range-perlin-noise.html
    const compensate3DScale = Math.sqrt(3)/Math.sqrt(2);
    const compensate4DScale = Math.sqrt(4)/Math.sqrt(2);
    
    function cylindernoise(noise, nx, ny) {
        let z = 0.0;
        let angle = TAU * nx;
        let cosAngle = Math.cos(angle),
            sinAngle = Math.sin(angle);
        for (let octave = 0, exponent = 1; octave < spectrum.length; octave++, exponent *= 2) {
            let n = compensate3DScale * noise[octave](cosAngle/TAU * exponent, sinAngle/TAU * exponent, ny * exponent);
            z += spectrum[octave] * (n/2 + 0.5);
        }
        return z / scale;
    }
    
    function torusnoise(noise, nx, ny) {
        let z = 0.0;
        let angle1 = TAU * nx,
            angle2 = TAU * ny;
        let cosAngle1 = Math.cos(angle1), sinAngle1 = Math.sin(angle1),
            cosAngle2 = Math.cos(angle2), sinAngle2 = Math.sin(angle2);
        for (let octave = 0, exponent = 1; octave < spectrum.length; octave++, exponent *= 2) {
            let n = compensate4DScale * noise[octave](cosAngle1/TAU * exponent, sinAngle1/TAU * exponent,
                                          cosAngle2/TAU * exponent, sinAngle2/TAU * exponent);
            z += spectrum[octave] * (n/2 + 0.5);
        }
        return z / scale;
    }

    let moisture = new Map2D(300, 300);
    let elevation = new Map2D(300, 300);
    function redraw() {
        for (let config of [
            {id: 'render-wraparound-ew', f: cylindernoise, eNoise: eNoise3, mNoise: mNoise3},
            {id: 'render-wraparound-nsew', f: torusnoise, eNoise: eNoise4, mNoise: mNoise4}
        ]) {
            let renderer = new Renderer2D(config.id, elevation,
                                          Colormap.discretebiomes(elevation, moisture));
            for (let y = 0; y < elevation.h; y++) {
                for (let x = 0; x < elevation.w; x++) {
                    let nx = (2 * x + elevation.w/2) % elevation.w / elevation.w, ny = (2 * y + elevation.h/2) % elevation.h / elevation.h;
                    let e = config.f(config.eNoise, nx, ny);
                    let m = config.f(config.mNoise, nx, ny);
                    if ((2 * x) % elevation.w === elevation.w / 2 || (2 * y) % elevation.h === elevation.h / 2) { e = 1; m = 1; }
                    elevation.set(x, y, e);
                    moisture.set(x, y, m);
                }
            }
            renderer.draw();
        }
    }
    redraw();
})();


// Dynamic diagram: allow scrolling of 2D map
(function() {
    const eseed = 12345, mseed = 123456;
    const efreq = 1, mfreq = 0.3;
    const espectrum = [1, 1/2, 1/4, 1/8, 1/16], mspectrum = [1, 1/2, 1/3, 1/4, 1/5, 1/4, 1/3, 1/5];
    const magnify = 7;

    // Draw the minimap
    let minielevation = new Map2D(150, 150);
    let minimoisture = new Map2D(150, 150);
    mixNoise(minielevation, espectrum, magnify * efreq, eseed);
    mixNoise(minimoisture, mspectrum, magnify * mfreq, mseed);
    let control = new Renderer2D('scrolling', minielevation, Colormap.discretebiomes(minielevation, minimoisture));
    let controlBox = document.querySelector('#infinite-selector');
    control.draw(); // can't use the cached img for events (leaky abstraction, sigh)
    
    // Draw the main map, reusing as much as possible between redraws
    let elevation = new Map2D(150, 150);
    let emixer = mixNoiseWithOffset(elevation, espectrum, efreq, eseed);
    let moisture = new Map2D(150, 150);
    let mmixer = mixNoiseWithOffset(moisture, mspectrum, mfreq, mseed);
    let height = new Map2D(150, 150);
    const exponent = 5;
    let colors = Colormap.discretebiomes(elevation, moisture);
    let renderer = new Renderer3D('render-scrolling-3d', height, colors);
    let dx = 0.0, dy = 0.0;
    
    // NOTE: the img src will have a timestamp appended on it by my build system
    let img = document.querySelector('img[src^="images/scrolling.png"]') ?? document.querySelector('canvas#scrolling');
    img.style.touchAction = "none";
    img.addEventListener('pointermove', function(e) {
        dx = (e.offsetX / img.clientWidth - 0.5) * magnify;
        dy = (e.offsetY / img.clientHeight - 0.5) * magnify;
        requestAnimationFrame(redraw);
    });

    function redraw() {
        /* TODO: correctly size and position the box
        const aspect = 2.0; // hard-coded in the html
        controlBox.style.width = `${img.clientWidth*aspect/magnify}px`;
        controlBox.style.height = `${img.clientWidth/magnify}px`;
        controlBox.style.marginLeft = `${-img.clientWidth*aspect/magnify/2}px`;
        controlBox.style.marginTop = `${-img.clientWidth/magnify/2}px`;
         */
        controlBox.style.left = `${img.clientWidth * (dx/magnify + 0.5)}px`;
        controlBox.style.top = `${img.clientHeight * (dy/magnify + 0.5)}px`;
        emixer(dx, dy);
        mmixer(dx, dy);
        reshapeNoiseExponent(height, elevation, exponent);
        renderer.draw();
    }
    redraw();
})();
